import { forwardRef, useEffect, useState } from "react";

/**
 * USDateInput — text input that displays dates in MM/DD/YYYY regardless of the
 * browser's locale, while emitting ISO `YYYY-MM-DD` to the parent so backend
 * payloads continue to work unchanged.
 *
 * Replaces native `<input type="date">` which renders MM/DD/YYYY only on
 * en-US-locale browsers (users on en-GB, fr-FR, etc. see DD/MM/YYYY).
 *
 * Behavior:
 * - Parent passes `value` as ISO ("2026-01-15") or empty string
 * - Component shows "01/15/2026" in the textbox
 * - As user types, auto-inserts slashes after MM and DD
 * - On blur, parses the masked text → emits ISO via `onChange`
 * - Invalid partial dates leave the parent's value unchanged
 *
 * Drop-in replacement:
 *   <USDateInput
 *     value={form.start_date}                         // "2026-01-15"
 *     onChange={(iso) => setForm({...form, start_date: iso})}
 *     data-testid="lease-start-date"
 *   />
 */

function isoToUS(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[2]}/${m[3]}/${m[1]}`;
}

function usToIso(us) {
  // Accept "M/D/YYYY", "MM/DD/YYYY", "MM/DD/YY"
  const m = String(us || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!m) return null;
  const mm = m[1].padStart(2, "0");
  const dd = m[2].padStart(2, "0");
  let yyyy = m[3];
  if (yyyy.length === 2) yyyy = (Number(yyyy) >= 50 ? "19" : "20") + yyyy;
  // Calendar sanity check
  const candidate = `${yyyy}-${mm}-${dd}`;
  const d = new Date(`${candidate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  // Reject silently if components round-trip differently (e.g. "02/30/2026")
  if (d.getUTCMonth() + 1 !== Number(mm) || d.getUTCDate() !== Number(dd)) return null;
  return candidate;
}

function autoInsertSlashes(raw) {
  // Strip non-digits-or-slashes; cap at 10 chars
  const cleaned = String(raw || "").replace(/[^\d/]/g, "").slice(0, 10);
  // Compute pure digits to know how far the user has typed
  const digits = cleaned.replace(/\D/g, "");
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

const USDateInput = forwardRef(function USDateInput(
  {
    value,
    onChange,
    className = "input-clean w-full font-mono",
    placeholder = "MM/DD/YYYY",
    onBlur,
    ...rest
  },
  ref
) {
  const [text, setText] = useState(() => isoToUS(value));

  // Keep local masked state in sync if the parent flips the iso value
  useEffect(() => {
    setText(isoToUS(value));
  }, [value]);

  const handleChange = (e) => {
    const masked = autoInsertSlashes(e.target.value);
    setText(masked);
    // Live-emit ISO when text is a complete valid date — useful for forms
    // that don't have a separate "save" button per field.
    const iso = usToIso(masked);
    if (iso) onChange?.(iso);
    else if (masked === "") onChange?.("");
  };

  const handleBlur = (e) => {
    const iso = usToIso(text);
    if (iso) {
      onChange?.(iso);
      setText(isoToUS(iso));
    } else if (text === "") {
      onChange?.("");
    } else {
      // Invalid partial — revert to last good iso
      setText(isoToUS(value));
    }
    onBlur?.(e);
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      maxLength={10}
      autoComplete="off"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      {...rest}
    />
  );
});

export default USDateInput;
